import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Spin, Typography } from 'antd'
import { find, includes, isEmpty, isNil } from 'lodash'

import { changeFavicon, showErrorNotification } from '../../../core/helpers'
import { usePrevious } from '../../../core/hooks'

import {
  CurationTaskAwaitingModal,
  Page,
} from '../../../common/components/sections'
import { StyledTaskPageHeader } from '../../../common/components/styled'
import {
  APP_ROUTES,
  EXCLUSION_STATUS,
  PERMISSIONS,
} from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks'

import { useUserHasPermissions } from '../../../account/hooks'
import { DishList } from '../../../dishes/components/sections'
import {
  MenuTitleIgnoredDishTypeModal,
  MenuTitlesTable,
} from '../../../menu-title/components/sections'
import {
  MENU_CURATION_TASK_STATUS,
  MENU_CURATION_TASK_TAGS,
} from '../../constants'
import {
  useLocationNamesLazyQuery,
  useMenuCurationTaskQuery,
  useUnblockMenuCurationTaskSubscription,
  useUpdateMenuCurationTaskMutation,
  useUserHasMenuCurationTaskChangePermission,
} from '../../hooks'
import { transformMenuCurationTaskFormValuesToUpdateArgs } from '../../transformers'
import {
  // MenuCurationTaskDetailLevelAndLanguageForm,
  MenuCurationTaskEditorForm,
  MenuCurationTaskTagsAndProblemsForm,
} from '../forms'
import {
  MenuCurationTaskDetailsAssignation,
  MenuCurationTaskHeader,
  MenuCurationTaskLabelLegends,
  MenuCurationTaskMenuButtons,
  MenuCurationTaskRetryButtons,
} from '../sections'

const MenuCurationTaskPage = () => {
  const { id } = useParams()
  useDocumentTitle(APP_ROUTES.MENUS.label)

  const { cuisineTypeOptions } = useSelector(
    store => store.common.metadata.data,
  )

  const [updateMenuCurationTask, { isUpdateLoading, updateError }] =
    useUpdateMenuCurationTaskMutation()

  const [fetchLocationNames, { error: locatioNamesError }] =
    useLocationNamesLazyQuery()

  const {
    data: menuCurationTask,
    error,
    isLoading,
    refetch,
  } = useMenuCurationTaskQuery(id)

  useUnblockMenuCurationTaskSubscription(refetch)

  const { userHasMenuChangePermission } =
    useUserHasMenuCurationTaskChangePermission(menuCurationTask)

  const hasScrapingReviewPermissions = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UPDATE_SCRAPING_REVIEW_STATUS,
  )

  const isCurationDisabled = useMemo(
    () => !userHasMenuChangePermission,
    [userHasMenuChangePermission],
  )

  const taskErrorStatus = useMemo(
    () =>
      find(menuCurationTask?.tags, tag =>
        includes(
          [
            MENU_CURATION_TASK_TAGS.HAS_TIMEOUT_ERROR.value,
            MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_AI_ERROR.value,
            MENU_CURATION_TASK_TAGS.HAS_UNCAUGHT_MENU_CURATOR_ERROR.value,
          ],
          tag,
        ),
      ),
    [menuCurationTask],
  )

  const taskExclusionStatus = useMemo(
    () =>
      menuCurationTask?.menu?.status === EXCLUSION_STATUS.EXCLUDED.value
        ? EXCLUSION_STATUS.EXCLUDED.value
        : null,
    [menuCurationTask],
  )

  const taskScrapingReviewStatus = useMemo(
    () =>
      !hasScrapingReviewPermissions &&
      includes(
        [
          MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW.value,
          MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.AWAITING_AUTOEAT_UPDATES.value,
        ],
        menuCurationTask?.taskStatus,
      )
        ? menuCurationTask?.taskStatus
        : null,
    [hasScrapingReviewPermissions, menuCurationTask?.taskStatus],
  )

  useEffect(() => {
    if (error || updateError || locatioNamesError) {
      showErrorNotification({
        title: !isNil(error)
          ? 'Failed to get menu curation task'
          : 'Failed to update menu curation task',
        description: error || updateError || locatioNamesError,
      })
    }
  }, [error, locatioNamesError, updateError])

  useEffect(() => {
    if (!isNil(menuCurationTask?.menu?.id)) {
      fetchLocationNames(menuCurationTask?.menu?.id)
    }
  }, [fetchLocationNames, menuCurationTask?.menu?.id])

  const previousAwaitingStatus = usePrevious(menuCurationTask?.awaitingStatus)
  useEffect(() => {
    if (menuCurationTask?.awaitingStatus) {
      changeFavicon('/favicon-circle-red.svg', 'image/svg+xml')
    }

    if (previousAwaitingStatus && !menuCurationTask?.awaitingStatus) {
      changeFavicon('/favicon-circle-green.svg', 'image/svg+xml')
    }
  }, [menuCurationTask?.awaitingStatus, previousAwaitingStatus])

  const handleSubmit = useCallback(
    formData => {
      updateMenuCurationTask(
        id,
        transformMenuCurationTaskFormValuesToUpdateArgs(formData),
      )
    },
    [id, updateMenuCurationTask],
  )

  if (error) {
    return <Typography.Text>Failed to get menu curation tasks</Typography.Text>
  }

  if (isLoading || isEmpty(menuCurationTask)) {
    return <Spin />
  }

  return (
    <Page>
      <StyledTaskPageHeader sticky>
        <MenuCurationTaskMenuButtons menuCurationTask={menuCurationTask} />
      </StyledTaskPageHeader>
      <CurationTaskAwaitingModal
        status={
          menuCurationTask?.awaitingStatus ||
          taskScrapingReviewStatus ||
          taskExclusionStatus ||
          taskErrorStatus
        }
        redirectTo={APP_ROUTES.MENUS.fullPath}
      />
      <MenuTitleIgnoredDishTypeModal />
      <MenuCurationTaskHeader menuCurationTask={menuCurationTask} />
      <MenuCurationTaskEditorForm
        menuCurationTask={menuCurationTask}
        cuisineTypeOptions={cuisineTypeOptions}
        onSubmit={handleSubmit}
        isLoading={isUpdateLoading}
        isDisabled={isCurationDisabled}
      />
      <MenuCurationTaskTagsAndProblemsForm
        menuCurationTask={menuCurationTask}
        onSubmit={handleSubmit}
        isLoading={isUpdateLoading}
        isDisabled={isCurationDisabled}
      />
      {/* <MenuCurationTaskDetailLevelAndLanguageForm
        menuCurationTask={menuCurationTask}
        onSubmit={handleSubmit}
        isLoading={isUpdateLoading}
        isDisabled={isCurationDisabled}
        languageOptions={languageOptions}
      /> */}
      <MenuCurationTaskDetailsAssignation menuCurationTask={menuCurationTask} />
      <MenuCurationTaskLabelLegends
        menuCurationTask={menuCurationTask}
        isDisabled={isCurationDisabled}
      />
      <MenuCurationTaskRetryButtons
        menuCurationTaskId={menuCurationTask.id}
        hasLockedDishes={menuCurationTask.hasLockedDishes}
        isDisabled={isCurationDisabled}
      />
      <MenuTitlesTable
        menuTitles={menuCurationTask?.menu?.menuTitles}
        isDisabled={isCurationDisabled}
        dietLabelLegends={menuCurationTask.dietLabelLegends}
        allergenLabelLegends={menuCurationTask.allergenLabelLegends}
      />
      <DishList
        menuCurationTask={menuCurationTask}
        isDisabled={isCurationDisabled}
      />
    </Page>
  )
}

export default MenuCurationTaskPage
